import { router } from 'expo-router';
import { useFocusEffect } from 'expo-router';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import {
  Button,
  ErrorBoundary,
  MqSwitch,
  NotFound,
  QuantityCounter,
  Skeleton,
  createMqStyles,
  tokens,
} from '@fhs/ui';
import { isWeb, useBoxRef } from '@fhs/utils';
import { useToast } from '@fhs-legacy/frontend/src/hooks/use-toast';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { isTest } from '@fhs-legacy/frontend/src/utils/environment';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import LocalStorage, { StorageKeys } from '@fhs-legacy/frontend/src/utils/local-storage';
import { parseStringifiedJSON } from '@fhs-legacy/frontend/src/utils/parse-string';
import { TOAST_ITEM_ADDED_TO_CART } from '@fhs-legacy/frontend/src/utils/test-ids';

import { PICKER_SELECTION_TYPE } from '../components/guide/constants';
import { MakeComboActionSheet } from '../components/make-combo-action-sheet';
import { MenuErrorFallback } from '../components/menu-error-fallback';
import { MenuItemDetails } from '../components/menu-item-details';
import { MenuItemSkeletonLoading } from '../components/menu-item-skeleton';
import { ServiceModeActionSheet } from '../components/service-mode-action-sheet';
import { useConfirmUnsavedCustomizationModal } from '../components/use-confirm-unsaved-customization-modal';
import { useComboSelectionContext } from '../state/combo-selection';
import { useMenuLayout } from '../state/menu-layout';
import { useMenuPicker } from '../state/menu-picker';
import {
  getComboSelectedItemIds,
  isComboAddToCartReady,
  willAddAsCombo,
} from '../utils/combo-selection-readiness';
import { useEditFromCartState } from '../utils/use-edit-from-cart-state';
import { useMenuItemPricePreview } from '../utils/use-menu-item-price-preview';
import { useTransformForAddToCart } from '../utils/use-transform-for-add-to-cart';

const MENU_ITEM_PAGE_MAX_QUANTITY = 10;

export function ScreenMenuItem({
  menuPicker,
  editFromCartState,
}: {
  menuPicker: ReturnType<typeof useMenuPicker>;
  editFromCartState: ReturnType<typeof useEditFromCartState> | null;
}) {
  const toast = useToast();
  const { formatMessage } = useIntl();

  const menuLayout = useMenuLayout();
  const { addItemToCart, setAddItemToCartLoading } = useOrderContext();

  const mqStyles = useMqStyles();
  const {
    selectedPickers,
    isComboEnabled,
    comboModalDisplayed,
    setComboModalDisplayed,
    guideSteps,
    pickerSelectionType,
  } = useComboSelectionContext();
  // Needed so we can access most up-to-date selectedPickers even in closures
  const comboSelectedPickersBoxRef = useBoxRef(selectedPickers);

  const [isPending, setIsPending] = useState(false);
  const [isShowingNutrition, setIsShowingNutrition] = useState(false);

  const [quantity, setQuantity] = useState(editFromCartState?.query.data?.quantity || 1);

  const { hasUnsavedCustomizationSelections, selectedPickerAspectOption } = menuPicker;
  const { confirmationDialog, openConfirmationDialog } = useConfirmUnsavedCustomizationModal();
  useFocusEffect(
    useCallback(() => {
      if (hasUnsavedCustomizationSelections && isWeb) {
        openConfirmationDialog();
      }
    }, [hasUnsavedCustomizationSelections, openConfirmationDialog])
  );

  const { formattedTotalPrice } = useMenuItemPricePreview({
    menuPicker,
    quantity,
  });
  const transformForAddToCart = useTransformForAddToCart();

  // Check fotter requirement
  const requiresPickAspectOptionSelection = menuPicker.visiblePickerAspectOptions.length > 1;

  const hasCustomizationGroups =
    (menuPicker.selectedPickerAspectOption?.item?.customizations?.displayGroups ?? []).length > 0;
  const hasCustomizationsContent = requiresPickAspectOptionSelection || hasCustomizationGroups;

  // Check if combos are required and user didn't make a selection yet
  const requiresComboSelection =
    menuPicker.isComboRequired &&
    Object.keys(selectedPickers).length < (menuPicker.query.data?.combo?.slots?.length ?? 0);

  const [showMakeComboActionSheet, setShowMakeComboActionSheet] = useState(false);

  const [isServiceModeOpen, setIsServiceModeOpen] = useState(false);
  const { noStoreSelected } = useStoreContext();
  const hasCombo = Boolean(menuPicker.query.data?.combo);
  const addingAsCombo = willAddAsCombo({
    hasCombo,
    isComboEnabled,
    isComboRequired: menuPicker.isComboRequired,
  });
  const comboSlotCount = menuPicker.query.data?.combo?.slots?.length ?? 0;
  const comboAddToCartReady = isComboAddToCartReady({
    willAddAsCombo: addingAsCombo,
    guideSteps,
    selectedPickers,
    isComboRequired: menuPicker.isComboRequired,
    comboSlotCount,
  });
  const hasComboItemIds = getComboSelectedItemIds(selectedPickers).length > 0;
  const isComboOrderWaitingForSelections =
    hasCombo && addingAsCombo && (!comboAddToCartReady || !hasComboItemIds);

  const handleAddToCart = async () => {
    if (noStoreSelected) {
      setIsServiceModeOpen(true);
      return;
    }
    // Always toggle show errors to on, it doesn't matter if there are existing error messages or not.
    menuPicker.setShowErrors(true);

    if (Object.keys(menuPicker.errorMessages).length) {
      // There are errors, don't submit
      return;
    }

    const currentComboPickers = comboSelectedPickersBoxRef.current;

    if (
      addingAsCombo &&
      (!comboAddToCartReady || getComboSelectedItemIds(currentComboPickers).length === 0)
    ) {
      return;
    }

    const itemId = menuPicker.selectedPickerAspectOption?.itemId;

    if (!itemId) {
      throw new Error('No itemId found');
    }
    setAddItemToCartLoading(true);
    setIsPending(true);

    transformForAddToCart({
      itemId,
      quantity,
      isCombo: addingAsCombo,
      selectedPickerAspectOption: menuPicker.selectedPickerAspectOption,
      resolvedCustomizationSelections: menuPicker.resolvedCustomizationSelections,
      pickerSelections: menuPicker.pickerSelections,
      comboSelectedPickers: currentComboPickers,
    })
      .then(transformedCartEntry => {
        if (!transformedCartEntry) {
          throw new Error('Cart Entries was empty');
        }
        const cartEntry = {
          ...transformedCartEntry,
          url: menuPicker.query.data?.url,
        };
        // Hopefully this is temporary...
        // Storing combo selected pickers by cartId to make sure combo selected pickers
        // are easily retrievable when editing items.
        // #region
        const cartId = (editFromCartState?.cartEntry?.cartId || cartEntry?.cartId) as string;
        const existingSelectedPickers = parseStringifiedJSON({
          value: LocalStorage.getItem(StorageKeys.ORDER_COMBO_SELECTED_PICKERS) || '',
          defaultValue: {},
        });

        LocalStorage.setItem(
          StorageKeys.ORDER_COMBO_SELECTED_PICKERS,
          JSON.stringify({
            ...existingSelectedPickers,
            ...{
              [cartId]: comboSelectedPickersBoxRef.current,
            },
          })
        );
        // #endregion

        if (menuLayout.isEditingThisItem) {
          menuLayout.onUpdate(cartEntry);
        } else {
          addItemToCart(cartEntry);
        }

        // Only dismiss to menu after successful add to cart
        if (!menuLayout.isEditingThisItem) {
          if (router.canGoBack()) {
            router.back();
          } else {
            router.navigate('/menu');
          }
        }
      })
      .catch(() => {
        toast.show({
          testID: TOAST_ITEM_ADDED_TO_CART,
          duration: isTest ? 9999999 : undefined,
          text: formatMessage({ id: 'addToCartError' }, { itemName: menuPicker.displayName }),
          variant: 'negative',
        });
        setAddItemToCartLoading(false);
      })
      .finally(() => {
        setIsPending(false);
        setAddItemToCartLoading(false);
      });
  };

  const enableComboDefaultSelection = useFlag('enable-menu-details-default-combo-selection');
  const isComboAvailable = !!menuPicker.query.data?.combo;

  const shouldDisableAddOrderButton =
    requiresComboSelection ||
    !selectedPickerAspectOption ||
    isComboOrderWaitingForSelections ||
    menuPicker.query.isFetching;

  const isComboPriceHydrating =
    editFromCartState?.query.data?.comboItems?.some(
      item => item.selectedCustomizationOptions?.length && !selectedPickers[item.slotId]
    ) ?? false;

  const showButtonLoadingSpinner =
    isPending || isComboOrderWaitingForSelections || isComboPriceHydrating;

  const itemA11yName = menuPicker.displayName || 'item';
  const atMaxQuantity = quantity >= MENU_ITEM_PAGE_MAX_QUANTITY;

  return (
    <View style={styles.screen}>
      <MakeComboActionSheet
        isOpen={showMakeComboActionSheet}
        onClose={() => setShowMakeComboActionSheet(false)}
        menuPicker={menuPicker}
        onCancel={() => handleAddToCart()}
      />
      {confirmationDialog}
      <ErrorBoundary fallback={<MenuErrorFallback />}>
        <View style={styles.flex1}>
          <MenuItemDetails
            isShowingNutrition={isShowingNutrition}
            setIsShowingNutrition={setIsShowingNutrition}
            handleAddToCart={handleAddToCart}
          />
        </View>

        <View style={styles.layout}>
          <View style={[mqStyles.footer, !hasCustomizationsContent && mqStyles.footerCenter]}>
            <QuantityCounter
              size="md"
              value={quantity}
              onChangeValue={newValue => setQuantity(newValue)}
              maxValue={MENU_ITEM_PAGE_MAX_QUANTITY}
              minValue={1}
              disabled={!selectedPickerAspectOption}
              label={formatMessage(
                { id: atMaxQuantity ? 'menuItemQuantityStepperLabelAtMax' : 'menuItemQuantityStepperLabel' },
                { name: itemA11yName, quantity, max: MENU_ITEM_PAGE_MAX_QUANTITY }
              )}
              decrementAccessibilityLabel={formatMessage(
                { id: 'menuItemDecrementQuantity' },
                { name: itemA11yName, quantity }
              )}
              incrementAccessibilityLabel={
                atMaxQuantity
                  ? formatMessage(
                      { id: 'menuItemIncrementQuantityAtMax' },
                      { name: itemA11yName, max: MENU_ITEM_PAGE_MAX_QUANTITY }
                    )
                  : formatMessage(
                      { id: 'menuItemIncrementQuantity' },
                      { name: itemA11yName, quantity }
                    )
              }
            >
              <QuantityCounter.Stepper />
            </QuantityCounter>
            <Button
              loading={showButtonLoadingSpinner}
              disabled={shouldDisableAddOrderButton || isPending}
              onPress={() => {
                if (
                  !enableComboDefaultSelection &&
                  isComboAvailable &&
                  pickerSelectionType !== PICKER_SELECTION_TYPE.COMBO &&
                  !noStoreSelected &&
                  !comboModalDisplayed &&
                  !editFromCartState?.isEditingThisItem
                ) {
                  setShowMakeComboActionSheet(true);
                  setComboModalDisplayed(true);

                  return;
                }
                handleAddToCart();
              }}
              style={[
                styles.flex1,
                mqStyles.addItemCTAButton,
                !hasCustomizationsContent && mqStyles.addItemCTAButtonCenter,
              ]}
              testID="menu-product-page-primary-action"
            >
              {isComboOrderWaitingForSelections || isComboPriceHydrating ? null : (
                <MqSwitch
                  $base={() => (
                    <View
                      style={[
                        mqStyles.ctaText,
                        styles.ctaContent,
                        (noStoreSelected || !formattedTotalPrice || isPending) && styles.justifyCenter,
                      ]}
                    >
                      {isPending && (
                        <View style={styles.ctaLoadingIndicator}>
                          <ActivityIndicator color={tokens.colors.$white} />
                        </View>
                      )}
                      {menuLayout.addText && (
                        <Button.Text style={styles.ctaLabelText}>
                          {menuLayout.addText}
                          {!isPending && formattedTotalPrice ? ' |' : ''}
                        </Button.Text>
                      )}
                      {!isPending &&
                        (menuPicker.query.isFetching ? (
                          <Skeleton
                            style={{
                              height: 16,
                              width: 80,
                              alignSelf: 'center',
                            }}
                          />
                        ) : (
                          formattedTotalPrice && (
                            <Button.Text style={mqStyles.priceText} testID="product-total-price">
                              {formattedTotalPrice}
                            </Button.Text>
                          )
                        ))}
                    </View>
                  )}
                  $gteDesktop={() => (
                    <View
                      style={[
                        styles.addItemToCartButton,
                        (noStoreSelected || !formattedTotalPrice || isPending) && styles.justifyCenter,
                      ]}
                    >
                      {isPending && (
                        <View style={styles.ctaLoadingIndicator}>
                          <ActivityIndicator color={tokens.colors.$white} />
                        </View>
                      )}
                      <Button.Text style={styles.ctaLabelText}>{menuLayout.addText}</Button.Text>
                      {!isPending &&
                        (menuPicker.query.isFetching ? (
                          <Skeleton
                            style={{
                              height: 16,
                              width: 80,
                              alignSelf: 'center',
                            }}
                          />
                        ) : (
                          formattedTotalPrice && (
                            <Button.Text style={styles.fontSize16} testID="product-total-price">
                              {formattedTotalPrice}
                            </Button.Text>
                          )
                        ))}
                    </View>
                  )}
                />
              )}
            </Button>
          </View>
        </View>
      </ErrorBoundary>
      <ServiceModeActionSheet
        isOpen={isServiceModeOpen}
        onClose={() => setIsServiceModeOpen(false)}
      />
    </View>
  );
}

export function ScreenMenuItemWithQuery() {
  const menuPicker = useMenuPicker();
  const editFromCartState = useEditFromCartState();
  const { guideSteps, selectedPickers, isComboEnabled } = useComboSelectionContext();

  const hasCombo = Boolean(menuPicker.query.data?.combo?.id);
  const addingAsCombo = willAddAsCombo({
    hasCombo,
    isComboEnabled,
    isComboRequired: menuPicker.isComboRequired,
  });
  const comboSlotCount = menuPicker.query.data?.combo?.slots?.length ?? 0;
  const comboAddToCartReady = isComboAddToCartReady({
    willAddAsCombo: addingAsCombo,
    guideSteps,
    selectedPickers,
    isComboRequired: menuPicker.isComboRequired,
    comboSlotCount,
  });
  const hasComboItemIds = getComboSelectedItemIds(selectedPickers).length > 0;

  const isComboAutoSelectionLoading =
    hasCombo &&
    addingAsCombo &&
    (!guideSteps?.length || !comboAddToCartReady || !hasComboItemIds);

  if (
    menuPicker.isLoading ||
    editFromCartState.query.isLoading ||
    isComboAutoSelectionLoading ||
    !menuPicker.query.isFetched
  ) {
    return (
      <View style={styles.screen}>
        <MenuItemSkeletonLoading />
      </View>
    );
  }

  // TODO: add refresh logic
  if (menuPicker.query.isError || editFromCartState.query.isError) {
    return (
      <View style={styles.screen}>
        <MenuErrorFallback />
      </View>
    );
  }

  if (menuPicker.query.data == null) {
    return <NotFound buttonText="Go To Menu" buttonUrl="/menu" />;
  }

  return (
    <ScreenMenuItem
      menuPicker={menuPicker}
      editFromCartState={editFromCartState.isEditingThisItem ? editFromCartState : null}
    />
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.$white,
  },
  flex1: {
    flex: 1,
  },
  layout: {
    backgroundColor: tokens.colors.$white,
    padding: 16,
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: tokens.colors.$blackOpacity10,
  },
  addItemToCartButton: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 12,
    position: 'relative',
  },
  justifyCenter: {
    justifyContent: 'center',
  },
  fontSize16: {
    fontSize: 16,
  },
  ctaLabelText: {
    fontSize: 16,
    flexShrink: 0,
  },
  ctaContent: {
    position: 'relative',
  },
  ctaLoadingIndicator: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
});

const useMqStyles = createMqStyles({
  footer: {
    $base: {
      flexDirection: 'row',
      alignSelf: 'center',
      justifyContent: 'center',
      width: '100%',
      gap: 28,
    },
    $gteDesktop: {
      justifyContent: 'flex-end',
      maxWidth: 1440,
      paddingRight: 52,
      gap: 52,
    },
  },
  footerCenter: {
    $gteDesktop: {
      justifyContent: 'space-between',
      maxWidth: 548,
      paddingRight: 0,
      gap: 52,
    },
  },
  addItemCTAButton: {
    $base: {
      maxWidth: 336,
    },
    $gteDesktop: {
      right: 26,
    },
  },
  addItemCTAButtonCenter: {
    $gteDesktop: {
      right: 0,
    },
  },
  ctaText: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 1,
      minWidth: 0,
      alignSelf: 'stretch',
    },
  },
  priceText: {
    $base: {
      fontSize: 16,
      left: -12,
    },
  },
});
